import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import type { GroupPricingFragmentFragment } from '@/features/group-pricing/gql/_gen_/group-pricing.gql';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { formatDate } from '../../date-utils';

interface LoadGroupDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	groups: GroupPricingFragmentFragment[];
	onSelectGroup: (groupId: string) => void;
	isLoading?: boolean;
}

export function LoadGroupDialog({
	open,
	onOpenChange,
	groups,
	onSelectGroup,
	isLoading = false,
}: LoadGroupDialogProps) {
	const [searchTerm, setSearchTerm] = useState('');

	// Filter groups based on search term
	const filteredGroups = groups.filter((group) => {
		if (!searchTerm.trim()) return true;

		const term = searchTerm.toLowerCase();
		return (
			group?.group_name?.toLowerCase().includes(term) ||
			group?.check_in?.toLowerCase().includes(term) ||
			group?.check_out?.toLowerCase().includes(term) ||
			group?.created_at?.toLowerCase().includes(term)
		);
	});

	// Format currency
	const formatCurrency = (value?: number) => {
		if (value == null) return '—';
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
		}).format(value);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:tw-max-w-[600px] tw-max-h-[80vh] tw-flex tw-flex-col'>
				<DialogHeader>
					<DialogTitle>Load Saved Group</DialogTitle>
					<DialogDescription>
						Select a saved group to load into the calculator
					</DialogDescription>
				</DialogHeader>

				{/* Search Input */}
				<div className='tw-relative tw-mb-4'>
					<Search className='tw-absolute tw-left-2 tw-top-2.5 tw-h-4 tw-w-4 tw-text-muted-foreground' />
					<Input
						placeholder='Search by name or date...'
						className='tw-pl-8'
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
				</div>

				{isLoading ? (
					<div className='tw-flex tw-justify-center tw-items-center tw-py-8'>
						<div className='tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-primary' />
					</div>
				) : filteredGroups.length === 0 ? (
					<div className='tw-text-center tw-py-8 tw-text-muted-foreground'>
						{groups.length === 0
							? 'No saved groups found. Create and save a group first.'
							: 'No groups match your search.'}
					</div>
				) : (
					<ScrollArea className='tw-flex-1 tw-pr-2 tw-h-[200px]'>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Check-in</TableHead>
									<TableHead>Check-out</TableHead>
									<TableHead>Rooms</TableHead>
									<TableHead>Rate</TableHead>
									<TableHead>Created By</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{filteredGroups.map((group) => (
									<TableRow
										key={group?.id}
										className='tw-cursor-pointer hover:tw-bg-muted'
										onClick={() => group?.id && onSelectGroup(group.id)}
									>
										<TableCell className='tw-font-medium'>
											{group?.group_name || '—'}
										</TableCell>
										<TableCell>
											{formatDate(group?.check_in, 'MMM d, yyyy')}
										</TableCell>
										<TableCell>
											{formatDate(group?.check_out, 'MMM d, yyyy')}
										</TableCell>
										<TableCell>{group?.rooms_requested || '—'}</TableCell>
										<TableCell>
											{formatCurrency(group?.rate_requested)}
										</TableCell>
										<TableCell>
											{group.user_email || group.user_id || '—'}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</ScrollArea>
				)}
			</DialogContent>
		</Dialog>
	);
}
